import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "../data/site";
import { SectionHeading } from "../components/SectionHeading";
import { Seo } from "../components/Seo";
import { SEO, breadcrumbSchema } from "../data/seo";
import { GalleryCard, GalleryLightbox } from "../components/SmileGalleryGrid";

const ITEMS_PER_PAGE = 12;
const SMILE_GALLERY_API_URL = "https://api.galleriadentalgrp.com/api/public/smile-gallery";
const SMILE_GALLERY_API_TOKEN =
  process.env.REACT_APP_TESTIMONIALS_API_TOKEN || "gd_23ec6c2817c94efb85ac1308d47b4a8c";

const getFirstString = (item, keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const getBestImageUrl = (imageDetail) => {
  if (!imageDetail || typeof imageDetail !== "object") {
    return "";
  }

  // Prefer full-size/original URLs when present to avoid pixelated thumbnails.
  return getFirstString(imageDetail, [
    "originalUrl",
    "original",
    "fullUrl",
    "full",
    "secure_url",
    "hdUrl",
    "imageUrl",
    "url",
    "thumbnail",
  ]);
};

const normalizeSmileGalleryImages = (item) => {
  const orderedImageDetails = Array.isArray(item?.imageDetails)
    ? item.imageDetails
        .filter(
          (imageDetail) =>
            imageDetail &&
            getBestImageUrl(imageDetail) &&
            Number.isFinite(imageDetail.order),
        )
        .sort((left, right) => left.order - right.order)
        .map((imageDetail) => getBestImageUrl(imageDetail))
    : [];

  if (orderedImageDetails.length > 0) {
    return orderedImageDetails;
  }

  return Array.isArray(item?.images)
    ? item.images.filter((image) => typeof image === "string" && image.trim())
    : [];
};

const normalizeSmileGalleryItem = (item, index) => {
  const images = normalizeSmileGalleryImages(item);

  return {
    id: item?._id || item?.id || `gallery-item-${index}`,
    category: getFirstString(item, ["category"]) || "Smile Gallery",
    label:
      getFirstString(item, ["label", "title", "description"]) || `Gallery ${index + 1}`,
    description: getFirstString(item, ["description"]),
    images,
  };
};



export const SmileGalleryPage = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const totalPages = Math.ceil(galleryItems.length / ITEMS_PER_PAGE);
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedItems = galleryItems.slice(startIdx, startIdx + ITEMS_PER_PAGE);
  const galleryRef = useRef(null);
  const isFirstRender = useRef(true);
  const hasLightboxHistoryEntry = useRef(false);

  const openLightbox = (globalIndex) => setLightboxIndex(globalIndex);
  const closeLightbox = useCallback(() => {
    if (hasLightboxHistoryEntry.current) {
      hasLightboxHistoryEntry.current = false;
      window.history.back();
      return;
    }

    setLightboxIndex(null);
  }, []);

  const goToPreviousPatient = useCallback(() => {
    setLightboxIndex((idx) => {
      if (idx === null || idx <= 0) return idx;
      return idx - 1;
    });
  }, []);

  const goToNextPatient = useCallback(() => {
    setLightboxIndex((idx) => {
      if (idx === null || idx >= galleryItems.length - 1) return idx;
      return idx + 1;
    });
  }, [galleryItems.length]);

  useEffect(() => {
    const controller = new AbortController();

    const loadGalleryItems = async () => {
      setGalleryLoading(true);

      try {
        const params = new URLSearchParams({
          limit: "100",
          category: "whitening",
          sort: "createdAt",
          order: "asc",
        });
        const response = await fetch(`${SMILE_GALLERY_API_URL}?${params.toString()}`, {
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${SMILE_GALLERY_API_TOKEN}`,
          },
        });
        const payload = await response.json();

        if (!response.ok || !payload?.success) {
          throw new Error(payload?.message || "Unable to load smile gallery.");
        }

        const rawItems = Array.isArray(payload?.data) ? payload.data : [];

        const items = rawItems
          .slice()
          .sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateA - dateB;
          })
          .map((item, index) => normalizeSmileGalleryItem(item, index))
          .filter((item) => item.images.length > 0);

        setGalleryItems(items);
        setCurrentPage(1);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        setGalleryItems([]);
      } finally {
        setGalleryLoading(false);
      }
    };

    loadGalleryItems();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (lightboxIndex === null) {
      return;
    }

    if (!galleryItems.length) {
      setLightboxIndex(null);
      return;
    }

    if (lightboxIndex >= galleryItems.length) {
      setLightboxIndex(galleryItems.length - 1);
    }
  }, [galleryItems, lightboxIndex]);

  useEffect(() => {
    if (lightboxIndex === null) {
      return;
    }

    // Add a synthetic entry so browser Back closes the popup first.
    window.history.pushState(
      {
        ...(window.history.state || {}),
        lightboxOpen: true,
      },
      "",
    );
    hasLightboxHistoryEntry.current = true;

    const onPopState = () => {
      if (!hasLightboxHistoryEntry.current) {
        return;
      }

      hasLightboxHistoryEntry.current = false;
      setLightboxIndex(null);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [lightboxIndex]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (galleryRef.current) {
      const top = galleryRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div data-testid="gallery-page">
      {lightboxIndex !== null && (
        <GalleryLightbox
          item={galleryItems[lightboxIndex]}
          onClose={closeLightbox}
          onPrevPatient={goToPreviousPatient}
          onNextPatient={goToNextPatient}
          hasPrevPatient={lightboxIndex > 0}
          hasNextPatient={lightboxIndex < galleryItems.length - 1}
          patientIndex={lightboxIndex}
          totalPatients={galleryItems.length}
        />
      )}

      <Seo
        title={SEO.gallery.title}
        description={SEO.gallery.description}
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Gallery", path: "/gallery" },
          ]),
        ]}
      />

      <section className="relative h-[40vh]  overflow-hidden">
        <img
          src={IMAGES.smilegallerybanner}
          alt="Smile Gallery Banner"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover "
        />
        <div className="absolute inset-0 bg-ink/40" />
        <div className="relative h-full container-wide flex flex-col justify-end pb-16 text-white">
          <div className="label-eyebrow text-gold mb-6">Smile Gallery</div>
          <h1 className="font-script heading-display text-5xl md:text-7xl text-white max-w-3xl">
            Smiles by <span className="italic text-gold-light">Dr.K.</span>
          </h1>
        </div>
      </section>

      <section className="py-5 container-x" >
        <p className="mb-8 text-center text-lg leading-relaxed text-ink/70 sm:text-xl md:text-2xl">
          Behind every smile there is a story.
        </p>

        {galleryLoading ? (
          <>
            <div className="mb-8 text-center">
              <p className="text-[11px] tracking-[0.3em] uppercase text-ink/45 font-mono">
                Loading Smile Gallery
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {Array.from({ length: 6 }, (_, index) => (
                <div key={index} className="aspect-square rounded-lg bg-ink/5 animate-pulse" />
              ))}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 mb-12">
            {paginatedItems.map((item, idx) => (
              <GalleryCard
                key={item.id}
                item={item}
                onOpen={() => openLightbox(startIdx + idx)}
              />
            ))}
          </div>
        )}

        {!galleryLoading && !galleryItems.length && (
          <div className="py-12 text-center text-ink/50 tracking-[0.2em] uppercase text-sm">
            No smile gallery images available right now.
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex flex-col items-center gap-4 mt-12">
            <div className="flex justify-center items-center gap-2 md:gap-4">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-ink/20 text-ink disabled:opacity-50 disabled:cursor-not-allowed hover:border-ink transition-colors"
              >
                ← Previous
              </button>

              <div className="flex gap-1 md:gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 md:px-4 py-2 text-sm transition-colors ${
                      currentPage === page
                        ? "bg-ink text-white border border-ink"
                        : "border border-ink/20 text-ink hover:border-ink"
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-ink/20 text-ink disabled:opacity-50 disabled:cursor-not-allowed hover:border-ink transition-colors"
              >
                Next →
              </button>
            </div>
            <p className="text-[11px] tracking-[0.3em] text-ink/40 font-mono">
              {startIdx + 1}–{Math.min(startIdx + ITEMS_PER_PAGE, galleryItems.length)} / {galleryItems.length}
            </p>
          </div>
        )}
      </section>

      <section className="py-24 bg-ink text-bone text-center">
        <div className="container-x">
          <SectionHeading title="Want to see " italicWord="your potential?" align="center" light />
          <p className="text-white/60 mt-6 max-w-md mx-auto">
            Send us a smiling photo. We'll send back a Digital Smile Design preview.
          </p>
          <Link to="/contact" data-testid="gallery-cta" className="btn-outline-light mt-10 inline-flex">
            Start a Virtual Consult
          </Link>
        </div>
      </section>
    </div>
  );
};
